// Review & send: the one look before a signing email leaves (Documents
// tab PR 8b; Nate Swartz 2026-09-19, "Before it goes").
//
// Design: docs/design/esign-send-confirmation.md, drawing
// docs/design/esign-send-confirmation-mockup-v1.html screens 2 and 3.
// Styles are ported from that file class by class (styles.css,
// `.esign-review*`).
//
// "Once I select to send to all parties to sign, I want a popup to
// summarize what is being sent and who it is being sent to. Show me the
// editable email that contains the copy that the client will see."
//
// This window holds no send state of its own. Everything it shows and
// edits (the note, the subject, the video) lives on Prepare & Send, so
// "Back to editing" loses nothing and the send that follows reads the
// same values this window showed. It never sends by itself either: the
// Send button hands back to Prepare & Send's own send, the one path.
//
// The grey lines are the email's stock wording (PR 8f): the library's
// "E-sign: signing invitation" template, drawn through the same renderer
// the server sends with (shared/esign/signing-invitation.ts), so what this
// window shows is what goes. "Edit these lines" changes them for this one
// send; the sign button stays in whatever the edit does.

import { useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  fromFriendlyInvitation,
  invitationBlocks,
  toFriendlyInvitation,
} from "@shared/esign/signing-invitation";

import { ApiError, apiV11 } from "../../api";
import { Button, useFocusTrap } from "../../ui";

/** A video as the window shows it; the server looks it up again at send. */
export interface ReviewVideo {
  url: string;
  title: string | null;
  durationSeconds: number | null;
  thumbnailUrl: string | null;
}

export interface ReviewSigner {
  key: string;
  name: string;
  email: string;
  /** The seat this person fills ("Seller 1"), when the send is cast. */
  role: string | null;
  /** Field count across the package, for "signs 9 fields". */
  fieldCount: number;
  /** The signer's colour on the prep screen, so the two agree. */
  color: string;
}

/** "When it completes" (Documents tab PR 12, mockup v3 step 4): where
 *  each output lands and what it checks off. Only a send from an
 *  Opportunity or Processing record has a Documents tab to describe. */
export interface ReviewCompletion {
  /** Where the signed copies land: the client's shelf when a household
   *  contact signs, otherwise Internal (signedCopyShelf, esign-archive). */
  signedShelf: "client" | "internal";
  /** Checklist items the package's forms will check off, by name. */
  checksOff: readonly string[];
}

export type ReviewSection = "documents" | "signers" | "copies" | "followups";

export interface ReviewAndSendProps {
  open: boolean;
  /** Back to editing. `section` names what the sender wants to change,
   *  so Prepare & Send can bring that part of its panel into view. */
  onBack: (section?: ReviewSection) => void;
  onSend: () => void;
  sending: boolean;
  documents: readonly { name: string; detail: string }[];
  signers: readonly ReviewSigner[];
  /** Seats the forms have that nobody fills; they print empty. */
  emptySeats?: readonly string[];
  completion?: ReviewCompletion | null;
  enforceOrder: boolean;
  /** The order in words when signers share numbers (PR 8d): "Robert &
   *  Linda together, then Mark". Null reads as one at a time. */
  orderLine?: string | null;
  copies: readonly { name: string; email: string }[];
  /** "Remind every 3 days, up to 3 times." or "No reminders." */
  followUps: string;
  /** "October 19", or null for no deadline. */
  deadline: string | null;
  from: { name: string; email: string | null; note: string };
  /** The sign-off a company-inbox send uses instead of a signature. */
  companySignOff: string | null;
  /** False when Outlook isn't connected: the links come back to copy. */
  willEmail: boolean;
  subject: string;
  defaultSubject: string;
  onSubjectChange: (subject: string) => void;
  note: string;
  onNoteChange: (note: string) => void;
  video: ReviewVideo | null;
  onVideoChange: (video: ReviewVideo | null) => void;
  /** The document the email names ("I've sent you X to sign"). */
  firstDocumentName: string;
  /** Injectable so a story never reaches Loom. */
  lookupVideo?: (url: string) => Promise<ReviewVideo>;
  /** The library's wording for the email's lines, with tokens (PR 8f).
   *  Null while it loads; the lines then wait rather than guess. */
  standardBody?: string | null;
  /** This send's own wording, or null for the standard. */
  emailBody?: string | null;
  onEmailBodyChange?: (body: string | null) => void;
  /** "Save this wording as my default for {kind} sends", where {kind} is
   *  this label ("SPDS", or "every" for an untyped document). */
  wordingDefaultLabel?: string | null;
  saveWordingDefault?: boolean;
  onSaveWordingDefault?: (on: boolean) => void;
  /** "Remember this video for the next {kind} I send on this file"; null
   *  hides it (a send with no file to remember it on). */
  rememberVideoLabel?: string | null;
  rememberVideo?: boolean;
  onRememberVideo?: (on: boolean) => void;
}

async function lookupThroughApi(url: string): Promise<ReviewVideo> {
  const res = await apiV11.lookupSigningVideo(url);
  return {
    url: res.url,
    title: res.title,
    durationSeconds: res.duration_seconds,
    thumbnailUrl: res.thumbnail_url,
  };
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? "";
}

/** "Robert", "Robert & Linda", "Robert, Linda & Sam", "4 people". */
export function signerNamesForButton(names: readonly string[]): string {
  const firsts = names.map(firstName).filter(Boolean);
  if (firsts.length === 0) return "them";
  if (firsts.length > 3) return `${firsts.length} people`;
  if (firsts.length === 1) return firsts[0]!;
  return `${firsts.slice(0, -1).join(", ")} & ${firsts[firsts.length - 1]}`;
}

function formatLength(seconds: number | null): string | null {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return null;
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function ReviewAndSend(props: ReviewAndSendProps) {
  const {
    open,
    onBack,
    onSend,
    sending,
    documents,
    signers,
    emptySeats = [],
    completion = null,
    enforceOrder,
    orderLine = null,
    copies,
    followUps,
    deadline,
    from,
    companySignOff,
    willEmail,
    subject,
    defaultSubject,
    onSubjectChange,
    note,
    onNoteChange,
    video,
    onVideoChange,
    firstDocumentName,
    lookupVideo = lookupThroughApi,
    standardBody = null,
    emailBody = null,
    onEmailBodyChange,
    wordingDefaultLabel = null,
    saveWordingDefault = false,
    onSaveWordingDefault,
    rememberVideoLabel = null,
    rememberVideo = false,
    onRememberVideo,
  } = props;
  const [editingLines, setEditingLines] = useState(false);
  const [tabKey, setTabKey] = useState<string | null>(null);
  const [addingVideo, setAddingVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(open, sheetRef);

  if (!open) return null;

  const tab = signers.find((s) => s.key === tabKey) ?? signers[0] ?? null;
  const names = signerNamesForButton(signers.map((s) => s.name));
  const count = signers.length;

  async function addVideo() {
    setVideoBusy(true);
    setVideoError(null);
    try {
      const found = await lookupVideo(videoUrl.trim());
      onVideoChange(found);
      setAddingVideo(false);
      setVideoUrl("");
    } catch (err) {
      setVideoError(
        err instanceof ApiError
          ? err.message
          : "That doesn't look like a Loom link. Copy the Share link from Loom and paste it here.",
      );
    } finally {
      setVideoBusy(false);
    }
  }

  const footerLine = willEmail
    ? `${count} ${count === 1 ? "email" : "emails"} will leave from ${from.note}${
        video ? ", each with the video" : ""
      }. Nothing has been sent.`
    : "Outlook isn't connected, so no email goes out: each person's link comes back for you to copy. Nothing has been sent.";

  const tree = (
    // Not the shared Modal: this window is a two-column sheet with its
    // own header line, and it must never close on an outside click (a
    // stray click would throw away a note typed in it). Escape and the
    // close button both mean "Back to editing", which loses nothing.
    <div className="overlay-backdrop esign-review-backdrop" role="presentation">
      <div
        ref={sheetRef}
        className="esign-review"
        role="dialog"
        aria-modal="true"
        aria-labelledby="esign-review-title"
        data-testid="esign-review"
        onKeyDown={(e) => {
          if (e.key === "Escape") onBack();
        }}
      >
        <div className="esign-review-header">
          <div>
            <h2 id="esign-review-title" className="esign-review-title">
              Before it goes
            </h2>
            <div className="esign-review-sub">
              This is exactly what {names} will get. Change anything here, or press Send.
            </div>
          </div>
          <button
            type="button"
            className="esign-review-close"
            aria-label="Close"
            onClick={() => onBack()}
          >
            ×
          </button>
        </div>

        <div className="esign-review-body">
          <div className="esign-review-sum">
            <div className="esign-review-sec">
              <div className="esign-review-sh">
                <h3>Sending</h3>
                <button
                  type="button"
                  className="esign-review-edit"
                  onClick={() => onBack("documents")}
                >
                  Edit
                </button>
              </div>
              {documents.map((d, i) => (
                <div className="esign-review-doc" key={`${d.name}-${i}`}>
                  <span className="esign-review-doc-ic" aria-hidden="true">
                    {documents.length > 1 ? i + 1 : ""}
                  </span>
                  <div>
                    <b>{d.name}</b>
                    <small>{d.detail}</small>
                  </div>
                </div>
              ))}
              {documents.length > 1 ? (
                <p className="esign-review-subline">They sign once, in this order.</p>
              ) : null}
            </div>

            <div className="esign-review-sec">
              <div className="esign-review-sh">
                <h3>Who signs</h3>
                <button
                  type="button"
                  className="esign-review-edit"
                  onClick={() => onBack("signers")}
                >
                  Edit
                </button>
              </div>
              {signers.map((s) => (
                <div className="esign-review-sg" key={s.key}>
                  <span className="esign-review-stripe" style={{ background: s.color }} />
                  <div>
                    <b>{s.name}</b>
                    <small>
                      {[
                        s.role,
                        s.email,
                        `${s.fieldCount} ${s.fieldCount === 1 ? "field" : "fields"}`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </small>
                  </div>
                  <span className="esign-review-kind">Signs</span>
                </div>
              ))}
              {emptySeats.map((seat) => (
                <div className="esign-review-sg esign-review-sg--empty" key={seat}>
                  <span className="esign-review-stripe esign-review-stripe--empty" />
                  <div>
                    <b>{seat}</b>
                    <small>Nobody in this seat · prints empty</small>
                  </div>
                </div>
              ))}
              <p className="esign-review-subline">
                {count === 1
                  ? "One email, with their own link."
                  : enforceOrder && orderLine
                    ? `In order: ${orderLine}. Each step is emailed once the one before has signed.`
                    : enforceOrder
                      ? "One at a time, in this order. The next person is emailed once the one before signs."
                      : "All at once. Each gets their own email and their own link."}
              </p>
            </div>

            <div className="esign-review-sec">
              <div className="esign-review-sh">
                <h3>Copies</h3>
                <button
                  type="button"
                  className="esign-review-edit"
                  onClick={() => onBack("copies")}
                >
                  Edit
                </button>
              </div>
              <p className="esign-review-line">
                {copies.length === 0 ? (
                  <>
                    The signed copy files to this record. <b>Nobody else</b> gets a copy.
                  </>
                ) : (
                  <>
                    The signed copy files to this record and goes to{" "}
                    <b>{copies.map((c) => c.name || c.email).join(", ")}</b>.
                  </>
                )}
              </p>
            </div>

            <div className="esign-review-sec">
              <div className="esign-review-sh">
                <h3>Follow-ups and deadline</h3>
                <button
                  type="button"
                  className="esign-review-edit"
                  onClick={() => onBack("followups")}
                >
                  Edit
                </button>
              </div>
              <p className="esign-review-line">
                {followUps}{" "}
                {deadline ? (
                  <>
                    Deadline <b>{deadline}</b>.
                  </>
                ) : (
                  "No deadline."
                )}
              </p>
              {willEmail && (note.trim() || video) ? (
                <p className="esign-review-subline">
                  Reminders carry this same note{video ? " and video" : ""}.
                </p>
              ) : null}
            </div>

            {completion ? (
              <div className="esign-review-sec" data-testid="esign-review-completion">
                <div className="esign-review-sh">
                  <h3>When it completes</h3>
                </div>
                <p className="esign-review-line">
                  The signed {documents.length === 1 ? "document goes" : "documents go"} on{" "}
                  <b>{completion.signedShelf === "client" ? "Visible to client" : "Internal"}</b>. The
                  certificate of completion goes on <b>Internal &amp; audit only</b>.
                </p>
                {completion.checksOff.length > 0 ? (
                  <p className="esign-review-line">
                    Checks off: <b>{completion.checksOff.join("; ")}</b>.
                  </p>
                ) : null}
                <p className="esign-review-subline">
                  This package leaves the Documents tab; the signed file is the record.
                </p>
              </div>
            ) : null}

            <div className="esign-review-sec">
              <div className="esign-review-sh">
                <h3>From</h3>
              </div>
              <p className="esign-review-line">
                <b>{from.name}</b>
                {from.email ? ` · ${from.email}` : ""} · {from.note}
              </p>
            </div>
          </div>

          <div className="esign-review-mail">
            <div className="esign-review-mail-head">
              <h3>
                {willEmail
                  ? "The email they will read"
                  : "The email (not sent: Outlook isn't connected)"}
              </h3>
              {signers.length > 1 ? (
                <div className="esign-review-tabs" role="tablist" aria-label="Whose email">
                  {signers.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      role="tab"
                      aria-selected={tab?.key === s.key}
                      className="esign-review-tab"
                      data-on={tab?.key === s.key ? "true" : undefined}
                      onClick={() => setTabKey(s.key)}
                    >
                      <span className="esign-review-dot" style={{ background: s.color }} />
                      {firstName(s.name) || s.email}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <label className="esign-review-subj">
              <span className="esign-review-subj-l">Subject</span>
              <input
                className="esign-review-subj-in"
                value={subject}
                maxLength={200}
                onChange={(e) => onSubjectChange(e.target.value)}
              />
            </label>

            <div className="esign-review-compose">
              {tab && firstName(tab.name) ? (
                <p className="esign-review-gen">
                  Hi {firstName(tab.name)},
                  {signers.length > 1 ? (
                    <span className="esign-review-tip">changes per person</span>
                  ) : null}
                </p>
              ) : null}
              <textarea
                className="esign-review-note"
                aria-label="Your note"
                placeholder="Add a note in your own words (optional). Links you paste become clickable."
                value={note}
                maxLength={2000}
                rows={4}
                onChange={(e) => onNoteChange(e.target.value)}
              />
              {addingVideo ? (
                <div className="esign-review-loom-edit">
                  <div className="esign-review-loom-row">
                    <input
                      className="esign-review-loom-in"
                      aria-label="Loom link"
                      placeholder="https://www.loom.com/share/…"
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && videoUrl.trim()) {
                          e.preventDefault();
                          void addVideo();
                        }
                      }}
                    />
                    <Button
                      variant="sw-navy"
                      size="sm"
                      disabled={!videoUrl.trim() || videoBusy}
                      onClick={() => void addVideo()}
                    >
                      {videoBusy ? "Looking…" : "Add"}
                    </Button>
                    <Button
                      variant="sw-ghost"
                      size="sm"
                      onClick={() => {
                        setAddingVideo(false);
                        setVideoError(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                  {videoError ? <p className="esign-review-loom-error">{videoError}</p> : null}
                </div>
              ) : null}
              {video ? (
                <div className="esign-review-loom-wrap">
                  {video.title ? (
                    <p className="esign-review-found">
                      <span className="esign-review-ok" aria-hidden="true">
                        ✓
                      </span>
                      Found on Loom: <b>“{video.title}”</b>
                      {formatLength(video.durationSeconds)
                        ? ` · ${formatLength(video.durationSeconds)}`
                        : ""}
                    </p>
                  ) : (
                    <p className="esign-review-found">
                      Loom didn't say what this video is, so the email shows a plain link to it.
                    </p>
                  )}
                  <a
                    className="esign-review-loom"
                    href={video.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span className="esign-review-loom-thumb">
                      {video.thumbnailUrl ? <img src={video.thumbnailUrl} alt="" /> : null}
                      <span className="esign-review-loom-play" aria-hidden="true" />
                      {formatLength(video.durationSeconds) ? (
                        <span className="esign-review-loom-len">
                          {formatLength(video.durationSeconds)}
                        </span>
                      ) : null}
                    </span>
                    <span className="esign-review-loom-meta">
                      <span className="esign-review-loom-k">
                        Video from {firstName(from.name) || "you"}
                      </span>
                      <b>{video.title ?? "Watch the video"}</b>
                      <small>Opens on Loom, no login needed</small>
                      <span className="esign-review-loom-go">Watch the video ›</span>
                    </span>
                  </a>
                  <button
                    type="button"
                    className="esign-review-quiet"
                    onClick={() => onVideoChange(null)}
                  >
                    Remove the video
                  </button>
                </div>
              ) : null}
              {editingLines && onEmailBodyChange ? (
                <div className="esign-review-lines-edit">
                  <textarea
                    className="esign-review-note"
                    aria-label="The email's lines"
                    rows={8}
                    maxLength={4000}
                    value={toFriendlyInvitation(emailBody ?? standardBody ?? "")}
                    onChange={(e) => {
                      const next = fromFriendlyInvitation(e.target.value);
                      onEmailBodyChange(next === standardBody ? null : next);
                    }}
                  />
                  <p className="esign-review-subline">
                    [document name], [deadline] and [Review &amp; sign button] fill in for each
                    person. The button stays in the email even if you delete its line.
                  </p>
                </div>
              ) : standardBody === null && emailBody === null ? (
                <p className="esign-review-gen">Loading the email's wording…</p>
              ) : (
                <button
                  type="button"
                  className="esign-review-lines"
                  data-changed={emailBody !== null ? "true" : undefined}
                  disabled={!onEmailBodyChange}
                  aria-label="Edit the email's lines for this send"
                  onClick={() => setEditingLines(true)}
                >
                  {invitationBlocks(emailBody ?? standardBody ?? "", {
                    documentName: firstDocumentName,
                    deadline,
                  }).map((b, i) =>
                    b.kind === "button" ? (
                      <span className="esign-review-btnchip" key={`b-${i}`}>
                        <span className="esign-review-btnchip-b">Review &amp; sign</span>
                        <span>
                          {tab
                            ? `${firstName(tab.name) || "Their"}'s own link goes here`
                            : "Their own link goes here"}
                        </span>
                        <span className="esign-review-lock">· stays in the email</span>
                      </span>
                    ) : (
                      <span className="esign-review-gen" key={`t-${i}`}>
                        {b.runs.map((r, j) =>
                          r.bold ? <b key={j}>{r.text}</b> : <span key={j}>{r.text}</span>,
                        )}
                      </span>
                    ),
                  )}
                </button>
              )}
              <div className="esign-review-sigline">
                {companySignOff ? (
                  <b>{companySignOff}</b>
                ) : (
                  <>
                    <b>{from.name}</b>
                    <span className="esign-review-sigtag">Your Outlook signature</span>
                  </>
                )}
              </div>
            </div>

            <div className="esign-review-toolbar">
              {!video && !addingVideo ? (
                <Button variant="sw-default" size="sm" onClick={() => setAddingVideo(true)}>
                  + Add a Loom video
                </Button>
              ) : null}
              {onEmailBodyChange && !editingLines && standardBody !== null ? (
                <Button variant="sw-default" size="sm" onClick={() => setEditingLines(true)}>
                  Edit the email's lines
                </Button>
              ) : null}
              {onEmailBodyChange && (emailBody !== null || editingLines) ? (
                <button
                  type="button"
                  className="esign-review-quiet"
                  onClick={() => {
                    onEmailBodyChange(null);
                    setEditingLines(false);
                  }}
                >
                  Reset to the standard wording
                </button>
              ) : null}
              {subject !== defaultSubject ? (
                <button
                  type="button"
                  className="esign-review-quiet"
                  onClick={() => onSubjectChange(defaultSubject)}
                >
                  Reset the subject
                </button>
              ) : null}
            </div>
            <div className="esign-review-keep">
              {onSaveWordingDefault && wordingDefaultLabel ? (
                <label className="esign-review-savedef">
                  <input
                    type="checkbox"
                    checked={saveWordingDefault}
                    onChange={(e) => onSaveWordingDefault(e.target.checked)}
                  />
                  {wordingDefaultLabel === "every"
                    ? "Save this wording as my default for every send"
                    : `Save this wording as my default for ${wordingDefaultLabel} sends`}
                </label>
              ) : null}
              {video && onRememberVideo && rememberVideoLabel ? (
                <label className="esign-review-savedef">
                  <input
                    type="checkbox"
                    checked={rememberVideo}
                    onChange={(e) => onRememberVideo(e.target.checked)}
                  />
                  {rememberVideoLabel === "every"
                    ? "Remember this video for the next one I send on this file"
                    : `Remember this video for the next ${rememberVideoLabel} I send on this file`}
                </label>
              ) : null}
            </div>
            <p className="esign-review-stew">
              <b>The note is yours to write.</b> The greeting, the document name, the button, the
              deadline and your signature fill in from the envelope, so they are always right. Click
              the grey lines to change them for this send.
              {video ? " The video goes above the sign button, and on every reminder too." : ""}
            </p>
          </div>
        </div>

        <div className="esign-review-footer">
          <span className="esign-review-footer-summary">{footerLine}</span>
          <div className="esign-review-footer-actions">
            <Button variant="sw-default" onClick={() => onBack()} disabled={sending}>
              Back to editing
            </Button>
            <Button variant="primary" onClick={onSend} disabled={sending || count === 0}>
              {sending ? "Sending…" : `Send to ${names}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
  // Portalled like Modal: Prepare & Send is its own fixed layer, and this
  // window has to sit above it, not inside its stacking context.
  if (typeof document === "undefined") return tree;
  return createPortal(tree, document.body);
}
